import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DataCollection from '../shared/DataCollection';
import FinanceCardList from './FinanceCardList.js';
import FinanceCategoryDropdown from '../shared/dropdowns/FinanceCategoryDropdown.js';
import FinanceInstitutionDropdown from '../shared/dropdowns/FinanceInstitutionDropdown.js';
import ProjectDropdown from '../shared/dropdowns/ProjectDropdown.js';
import TextInput from '../shared/inputs/TextInput.js';
import DateInput from '../shared/inputs/DateInput.js';
import Patch from '../utils/Patch.js';
import { Fetch } from '../utils/Utils.js';
import '../herd/ListHerd.css';
import './ListFinances.css';

export const ListFinances = ({ currencySymbol = 'R$' }) => {
  const { t } = useTranslation();
  const [refreshKey,   setRefreshKey]   = useState(0);
  const [projects,     setProjects]     = useState([]);
  const [categories,   setCategories]   = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [isMobile,     setIsMobile]     = useState(window.innerWidth < 768);

  // Filters (Trello CxlkikLP) — client-side, same filter bar as Herd →
  // Animals. They live here rather than in DataCollection, which remounts
  // after every inline edit (key={refreshKey}), so they survive edits.
  const [search,            setSearch]            = useState('');
  const [projectFilter,     setProjectFilter]     = useState(''); // '' all, 'none', or a project _id
  const [typeFilter,        setTypeFilter]        = useState('');
  const [categoryFilter,    setCategoryFilter]    = useState('');
  const [institutionFilter, setInstitutionFilter] = useState('');
  const [dateFrom,          setDateFrom]          = useState('');
  const [dateTo,            setDateTo]            = useState('');
  const [filtersOpen,        setFiltersOpen]        = useState(false);
  const [desktopFiltersOpen, setDesktopFiltersOpen] = useState(false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Loaded once and shared by every row's dropdowns and the filters.
  useEffect(() => {
    Fetch('api/finance/projects', 'GET', null, (data) => setProjects(data || []), () => {});
    Fetch('api/data/finance.categories', 'GET', null, (data) => setCategories(data || []), () => {});
    Fetch('api/data/finance.institutions', 'GET', null, (data) => setInstitutions(data || []), () => {});
  }, []);

  const setProject = (row, projectId) =>
    Patch('finance.transactions', row._id, 'project_id', projectId,
      () => setRefreshKey(k => k + 1),
      () => {}
    );

  const filterRow = (row) => {
    if (projectFilter === 'none' ? row.project_id : (projectFilter && row.project_id !== projectFilter)) return false;
    if (typeFilter && row.type !== typeFilter) return false;
    if (categoryFilter && row.category !== categoryFilter) return false;
    if (institutionFilter && row.institution !== institutionFilter) return false;
    const date = (row.date || '').substring(0, 10);
    if (dateFrom && date < dateFrom) return false;
    if (dateTo && date > dateTo) return false;
    if (search) {
      const q = search.toLowerCase();
      const text = [
        row.notes,
        row.category && t(row.category, { defaultValue: row.category }),
        row.institution && t(row.institution, { defaultValue: row.institution }),
        row.created_by,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!text.includes(q)) return false;
    }
    return true;
  };

  const activeFilterCount = [typeFilter, categoryFilter, institutionFilter, dateFrom, dateTo]
    .filter(v => v !== '').length;

  const resetFilters = () => {
    setTypeFilter('');
    setCategoryFilter('');
    setInstitutionFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const projectSelect = (
    <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)}>
      <option value="">{t('all_projects')}</option>
      <option value="none">{t('no_project')}</option>
      {projects.map(p => (
        <option key={p._id} value={p._id}>{p.code} — {p.name}</option>
      ))}
    </select>
  );

  const filterPanel = (
    <>
      <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
        <option value="">{t('all_types')}</option>
        <option value="gain">{t('gain')}</option>
        <option value="loss">{t('loss')}</option>
      </select>
      <FinanceCategoryDropdown showAll allLabel={t('all_categories')} categories={categories}
        selected={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} />
      <FinanceInstitutionDropdown showAll allLabel={t('all_institutions')} institutions={institutions}
        selected={institutionFilter} onChange={e => setInstitutionFilter(e.target.value)} />
      <DateInput value={dateFrom} onChange={setDateFrom} name="date_from"
        required={false} defaultToToday={false} placeholder={t('date_from')} />
      <DateInput value={dateTo} onChange={setDateTo} name="date_to"
        required={false} defaultToToday={false} placeholder={t('date_to')} />
    </>
  );

  const columns = [
    {
      name: t('type'),
      selector: row => row.type,
      sortable: true,
      cell: row => (
        <span style={{ color: row.type === 'gain' ? '#2d7a2d' : '#c0392b', fontWeight: 600 }}>
          {row.type === 'gain' ? t('gain') : t('loss')}
        </span>
      ),
    },
    {
      name: t('amount'),
      selector: row => row.amount,
      sortable: true,
      cell: row => (
        <span style={{ color: row.type === 'gain' ? '#2d7a2d' : '#c0392b', fontWeight: 600 }}>
          {row.type === 'gain' ? '+' : '-'}{currencySymbol}{Number(row.amount || 0).toFixed(2)}
        </span>
      ),
    },
    {
      name: t('category'),
      selector: row => row.category || '—',
      sortable: true,
      cell: row => (
        <FinanceCategoryDropdown
          categories={categories}
          selected={row.category}
          onChange={(e) => Patch('finance.transactions', row._id, 'category', e.target.value,
            () => setRefreshKey(k => k + 1),
            () => {}
          )}
        />
      ),
    },
    {
      name: t('institution'),
      selector: row => row.institution ? t(row.institution, { defaultValue: row.institution }) : '—',
      sortable: true,
      cell: row => (
        <FinanceInstitutionDropdown
          institutions={institutions}
          selected={row.institution}
          onChange={(e) => Patch('finance.transactions', row._id, 'institution', e.target.value,
            () => setRefreshKey(k => k + 1),
            () => {}
          )}
        />
      ),
    },
    {
      name: t('project'),
      selector: row => projects.find(p => p._id === row.project_id)?.code || '',
      sortable: true,
      cell: row => (
        <ProjectDropdown
          projects={projects}
          selected={row.project_id}
          onChange={(e) => setProject(row, e.target.value)}
        />
      ),
    },
    {
      name: t('notes'),
      selector: row => row.notes || '',
      cell: row => (
        <TextInput
          value={row.notes || ''}
          onBlur={(e) => Patch('finance.transactions', row._id, 'notes', e.target.value,
            () => setRefreshKey(k => k + 1),
            () => {}
          )}
        />
      ),
    },
    { name: t('date'),      selector: row => row.date?.substring(0, 10) || '', sortable: true },
    { name: t('who'),       selector: row => row.created_by || '—' },
  ];

  return (
    <div className="ListHerd">
      {isMobile ? (
        <div className="mobile-search-filter">
          <input
            className="mobile-search-input"
            type="text"
            placeholder={t('search_transactions')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button
            className={`mobile-filter-toggle ${filtersOpen ? 'open' : ''} ${activeFilterCount > 0 || projectFilter ? 'active' : ''}`}
            onClick={() => setFiltersOpen(o => !o)}
          >
            {t('filters')}
            {activeFilterCount > 0 && <span className="mobile-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>
      ) : (
        <div className="desktop-filter-bar">
          <div className="desktop-filter-top">
            <input
              type="text"
              className="desktop-search-input"
              placeholder={t('search_transactions')}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {projectSelect}
            <button
              className={`desktop-filter-toggle ${desktopFiltersOpen ? 'open' : ''} ${activeFilterCount > 0 ? 'active' : ''}`}
              onClick={() => setDesktopFiltersOpen(o => !o)}
            >
              {t('filters')} {desktopFiltersOpen ? '▲' : '▼'}
              {activeFilterCount > 0 && <span className="desktop-filter-badge">{activeFilterCount}</span>}
            </button>
            {activeFilterCount > 0 && (
              <button className="desktop-filter-reset" onClick={resetFilters}>
                {t('reset_filters')}
              </button>
            )}
          </div>
          {desktopFiltersOpen && (
            <div className="desktop-filter-panel">{filterPanel}</div>
          )}
        </div>
      )}

      <DataCollection
        key={refreshKey}
        collection="finance.transactions"
        columns={columns}
        sortField={7}
        sortAsc={false}
        filterRow={filterRow}
        mobileComponent={(data, onRefresh) => (
          <FinanceCardList data={data} onRefresh={onRefresh} currencySymbol={currencySymbol}
            projects={projects} onProjectChange={(row, projectId) => setProject(row, projectId)} />
        )}
      />

      {filtersOpen && (
        <>
          <div className="filter-sheet-backdrop" onClick={() => setFiltersOpen(false)} />
          <div className="filter-sheet">
            <div className="filter-sheet-handle" />
            <div className="filter-sheet-header">
              <span className="filter-sheet-title">{t('filters')}</span>
              <button className="filter-sheet-close" onClick={() => setFiltersOpen(false)}>✕</button>
            </div>
            <div className="filter-sheet-body">
              {projectSelect}
              {filterPanel}
            </div>
            <div className="filter-sheet-footer">
              <button
                className="filter-sheet-reset"
                onClick={resetFilters}
                disabled={activeFilterCount === 0}
              >
                {t('reset_filters')} {activeFilterCount > 0 && `(${activeFilterCount})`}
              </button>
              <button className="filter-sheet-apply" onClick={() => setFiltersOpen(false)}>
                {t('done')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ListFinances;
